import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  CommentState,
  CreateCommentRequest,
  UpdateCommentRequest,
  CommentListParams
} from '@/constants/types';
import { commentService } from '@/services/comments/commentService';

const initialState: CommentState = {
  currentFeedId: null,
  comments: [],
  pagination: null,
  isLoading: false,
  isLoadingMore: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
};

// Create Comment
export const createComment = createAsyncThunk(
  'comments/createComment',
  async (commentData: CreateCommentRequest, { rejectWithValue }) => {
    try {
      const response = await commentService.createComment(commentData);
      console.log('createComment response:', response);
      
      // Handle different response structures
      if ((response as any)?.data?.data?.comment) {
        return (response as any).data.data.comment;
      } else if ((response as any)?.data?.comment) {
        return (response as any).data.comment;
      } else if ((response as any)?.comment) {
        return (response as any).comment;
      }
      
      return response as any;
    } catch (error: unknown) {
      console.error('createComment error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create comment';
      return rejectWithValue(errorMessage);
    }
  }
);

// Fetch Comments by Feed ID (first page or refresh)
export const fetchCommentsByFeedId = createAsyncThunk(
  'comments/fetchCommentsByFeedId',
  async ({ feedId, params = {} }: { feedId: string; params?: CommentListParams }, { rejectWithValue }) => {
    try {
      const response = await commentService.getCommentsByFeedId(feedId, params);
      console.log('fetchCommentsByFeedId response:', response);
      
      // Handle different response structures
      let comments: Comment[] = [];
      let pagination = null;
      
      if ((response as any)?.data?.data?.comments) {
        // Nested structure: response.data.data.comments
        comments = (response as any).data.data.comments;
        pagination = (response as any).data.data.pagination;
      } else if ((response as any)?.data?.comments) {
        // Single nested: response.data.comments
        comments = (response as any).data.comments;
        pagination = (response as any).data.pagination;
      } else if ((response as any)?.comments) {
        // Direct: response.comments
        comments = (response as any).comments;
        pagination = (response as any).pagination;
      }
      
      return { 
        feedId,
        comments: comments || [], 
        pagination: pagination || null
      };
    } catch (error: unknown) {
      console.error('fetchCommentsByFeedId error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch comments';
      return rejectWithValue(errorMessage);
    }
  }
);

// Load More Comments (next page)
export const loadMoreComments = createAsyncThunk(
  'comments/loadMoreComments',
  async ({ feedId, params = {} }: { feedId: string; params?: CommentListParams }, { rejectWithValue }) => {
    try {
      const response = await commentService.getCommentsByFeedId(feedId, params);
      console.log('loadMoreComments response:', response);
      
      // Handle different response structures
      let comments: Comment[] = [];
      let pagination = null;
      
      if ((response as any)?.data?.data?.comments) {
        // Nested structure: response.data.data.comments
        comments = (response as any).data.data.comments;
        pagination = (response as any).data.data.pagination;
      } else if ((response as any)?.data?.comments) {
        // Single nested: response.data.comments
        comments = (response as any).data.comments;
        pagination = (response as any).data.pagination;
      } else if ((response as any)?.comments) {
        // Direct: response.comments
        comments = (response as any).comments;
        pagination = (response as any).pagination;
      }
      
      return { 
        comments: comments || [], 
        pagination: pagination || null
      };
    } catch (error: unknown) {
      console.error('loadMoreComments error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load more comments';
      return rejectWithValue(errorMessage);
    }
  }
);

// Update Comment
export const updateComment = createAsyncThunk(
  'comments/updateComment',
  async ({ commentId, commentData }: { 
    commentId: string; 
    commentData: UpdateCommentRequest; 
  }, { rejectWithValue }) => {
    try {
      const response = await commentService.updateComment(commentId, commentData);
      console.log('updateComment response:', response);
      
      // Handle different response structures
      let comment = null;
      if ((response as any)?.data?.data?.comment) {
        comment = (response as any).data.data.comment;
      } else if ((response as any)?.data?.comment) {
        comment = (response as any).data.comment;
      } else if ((response as any)?.comment) {
        comment = (response as any).comment;
      } else {
        comment = response;
      }
      
      return { comment, commentId };
    } catch (error: unknown) {
      console.error('updateComment error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update comment';
      return rejectWithValue(errorMessage);
    }
  }
);

// Delete Comment
export const deleteComment = createAsyncThunk(
  'comments/deleteComment',
  async (commentId: string, { rejectWithValue }) => {
    try {
      await commentService.deleteComment(commentId);
      return commentId;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete comment';
      return rejectWithValue(errorMessage);
    }
  }
);

const commentSlice = createSlice({
  name: 'comments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetCommentState: () => initialState,
    clearComments: (state) => {
      state.currentFeedId = null;
      state.comments = [];
      state.pagination = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Comment
      .addCase(createComment.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createComment.fulfilled, (state, action) => {
        state.isCreating = false;
        // Add new comment to the beginning (latest first)
        state.comments.unshift(action.payload);
        // Update total count in pagination
        if (state.pagination) {
          state.pagination.total += 1;
        }
        state.error = null;
      })
      .addCase(createComment.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })
      
      // Fetch Comments (first page)
      .addCase(fetchCommentsByFeedId.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCommentsByFeedId.fulfilled, (state, action) => {
        state.isLoading = false;
        const { feedId, comments, pagination } = action.payload;
        
        
        // Set current feed and replace comments (fresh fetch)
        state.currentFeedId = feedId;
        state.comments = comments as any;
        state.pagination = pagination;
        state.error = null;
      })
      .addCase(fetchCommentsByFeedId.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Load More Comments (additional pages)
      .addCase(loadMoreComments.pending, (state) => {
        state.isLoadingMore = true;
        state.error = null;
      })
      .addCase(loadMoreComments.fulfilled, (state, action) => {
        state.isLoadingMore = false;
        const { comments, pagination } = action.payload;
        
        // Append new comments to existing ones
        state.comments = [...state.comments, ...comments] as any;
        state.pagination = pagination;
        state.error = null;
      })
      .addCase(loadMoreComments.rejected, (state, action) => {
        state.isLoadingMore = false;
        state.error = action.payload as string;
      })
      
      // Update Comment
      .addCase(updateComment.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateComment.fulfilled, (state, action) => {
        state.isUpdating = false;
        const { comment, commentId } = action.payload;
        
        const index = state.comments.findIndex(c => c._id === commentId);
        if (index !== -1) {
          state.comments[index] = { ...state.comments[index], ...comment };
        }
        state.error = null;
      })
      .addCase(updateComment.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      
      // Delete Comment
      .addCase(deleteComment.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        state.isDeleting = false;
        const commentId = action.payload;
        
        // Remove comment from array
        state.comments = state.comments.filter(c => c._id !== commentId);
        // Update total count in pagination
        if (state.pagination) {
          state.pagination.total -= 1;
        }
        state.error = null;
      })
      .addCase(deleteComment.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, resetCommentState, clearComments } = commentSlice.actions;
export default commentSlice.reducer;